import { BaseComponent } from '../lib/BaseComponent.js';
import { api } from '../lib/api.js';
import { router } from '../lib/router.js';

/**
 * Register Page Component
 * Handles new organization and user registration
 */
export class RegisterPage extends BaseComponent {
  private isLoading = false;
  private errorMessage = '';

  protected render(): void {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'register-container' });

    // Logo and title
    const header = this.createElement('div', { className: 'register-header' });
    const title = this.createElement('h1', {}, 'FireGuard');
    const subtitle = this.createElement('p', { className: 'subtitle' }, 'Start your 45-day free trial. No credit card.');
    header.appendChild(title);
    header.appendChild(subtitle);
    container.appendChild(header);

    // Register form card
    const card = this.createElement('div', { className: 'register-card' });
    const cardTitle = this.createElement('h2', {}, 'Create your account');
    card.appendChild(cardTitle);

    // Error message
    if (this.errorMessage) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage);
      card.appendChild(error);
    }

    // Form
    const form = this.createElement('form', { className: 'register-form' });
    form.addEventListener('submit', (e) => this.handleSubmit(e));

    // Organization name field
    const orgGroup = this.createElement('div', { className: 'form-group' });
    const orgLabel = this.createElement('label', { for: 'organizationName' }, 'Organization Name');
    const orgInput = this.createElement('input', {
      type: 'text',
      id: 'organizationName',
      name: 'organizationName',
      required: 'true',
      placeholder: 'Acme Corp',
    });
    orgGroup.appendChild(orgLabel);
    orgGroup.appendChild(orgInput);
    form.appendChild(orgGroup);

    // Name field
    const nameGroup = this.createElement('div', { className: 'form-group' });
    const nameLabel = this.createElement('label', { for: 'name' }, 'Your Name');
    const nameInput = this.createElement('input', {
      type: 'text',
      id: 'name',
      name: 'name',
      required: 'true',
      placeholder: 'John Doe',
    });
    nameGroup.appendChild(nameLabel);
    nameGroup.appendChild(nameInput);
    form.appendChild(nameGroup);

    // Email field
    const emailGroup = this.createElement('div', { className: 'form-group' });
    const emailLabel = this.createElement('label', { for: 'email' }, 'Work Email');
    const emailInput = this.createElement('input', {
      type: 'email',
      id: 'email',
      name: 'email',
      required: 'true',
      placeholder: 'admin@acme.com',
    });
    emailGroup.appendChild(emailLabel);
    emailGroup.appendChild(emailInput);
    form.appendChild(emailGroup);

    // Password field
    const passwordGroup = this.createElement('div', { className: 'form-group' });
    const passwordLabel = this.createElement('label', { for: 'password' }, 'Password');
    const passwordInput = this.createElement('input', {
      type: 'password',
      id: 'password',
      name: 'password',
      required: 'true',
      placeholder: '••••••••',
      minlength: '8',
    });
    const passwordHint = this.createElement('span', { className: 'hint' }, 'Minimum 8 characters');
    passwordGroup.appendChild(passwordLabel);
    passwordGroup.appendChild(passwordInput);
    passwordGroup.appendChild(passwordHint);
    form.appendChild(passwordGroup);

    // Terms acceptance (required)
    const termsGroup = this.createElement('div', { className: 'form-group terms-group' });
    const termsLabel = this.createElement('label', { for: 'acceptTerms', className: 'checkbox-label' });
    const termsInput = this.createElement('input', { type: 'checkbox', id: 'acceptTerms', name: 'acceptTerms', required: 'true' });
    termsLabel.appendChild(termsInput);
    termsLabel.appendChild(document.createTextNode(' I agree to the '));
    termsLabel.appendChild(this.createElement('a', { href: '/terms-of-service', target: '_blank', rel: 'noopener' }, 'Terms of Service'));
    termsLabel.appendChild(document.createTextNode(' and '));
    termsLabel.appendChild(this.createElement('a', { href: '/privacy-policy', target: '_blank', rel: 'noopener' }, 'Privacy Policy'));
    termsLabel.appendChild(document.createTextNode('.'));
    termsGroup.appendChild(termsLabel);
    form.appendChild(termsGroup);

    // Submit button
    const submitBtn = this.createElement('button', {
      type: 'submit',
      className: 'btn-primary',
    }, this.isLoading ? 'Creating account...' : 'Start free trial');
    if (this.isLoading) {
      submitBtn.setAttribute('disabled', 'true');
    }
    form.appendChild(submitBtn);

    card.appendChild(form);

    // Login link
    const footer = this.createElement('div', { className: 'register-footer' });
    const footerText = this.createElement('p', {}, 'Already have an account? ');
    const loginLink = this.createElement('a', { href: '/login' }, 'Sign In');
    footerText.appendChild(loginLink);
    footer.appendChild(footerText);

    // Legal links
    const legalText = this.createElement('p', { className: 'legal-links' });
    const privacyLink = this.createElement('a', { href: '/privacy-policy' }, 'Privacy Policy');
    const separator = document.createTextNode(' • ');
    const termsLink = this.createElement('a', { href: '/terms-of-service' }, 'Terms of Service');
    legalText.appendChild(privacyLink);
    legalText.appendChild(separator);
    legalText.appendChild(termsLink);
    footer.appendChild(legalText);

    card.appendChild(footer);

    container.appendChild(card);
    this.shadow.appendChild(container);
  }

  private async handleSubmit(e: Event): Promise<void> {
    e.preventDefault();

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const organizationName = formData.get('organizationName') as string;
    const name = formData.get('name') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const acceptTerms = formData.get('acceptTerms') === 'on';
    if (!acceptTerms) {
      this.errorMessage = 'Please accept the Terms of Service to continue.';
      this.shadow.replaceChildren();
      this.render();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.shadow.innerHTML = '';
    this.render();

    try {
      await api.register({ organizationName, name, email, password, acceptTerms: true, termsVersion: '2026-09' });
      // Navigate to dashboard on success; carry a plan hint through to billing when one was chosen on the pricing page
      const plan = new URLSearchParams(window.location.search).get('plan');
      router.navigate(plan ? `/billing?plan=${encodeURIComponent(plan)}` : '/dashboard');
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Registration failed';
      this.shadow.innerHTML = '';
      this.render();
    }
  }

  private getStyles(): string {
    return `
      .checkbox-label { display: flex; gap: 8px; align-items: flex-start; font-size: 14px; font-weight: 400; color: var(--gray-700); line-height: 1.4; }
      .checkbox-label input { margin-top: 3px; }
      .checkbox-label a { color: var(--primary); }
      .register-container {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        background: linear-gradient(135deg, var(--gray-50) 0%, var(--gray-100) 100%);
        padding: var(--spacing-lg);
      }

      .register-header {
        text-align: center;
        margin-bottom: var(--spacing-lg);
      }

      .register-header h1 {
        font-size: 48px;
        font-weight: 700;
        color: var(--danger);
        margin-bottom: var(--spacing-sm);
      }

      .subtitle {
        font-size: 16px;
        color: var(--gray-500);
      }

      .register-card {
        background: white;
        border-radius: var(--border-radius);
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        padding: 32px;
        width: 100%;
        max-width: 400px;
      }

      .register-card h2 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-lg);
        text-align: center;
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
        font-size: 14px;
      }

      .register-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      label {
        font-size: 14px;
        font-weight: 500;
        color: var(--gray-700);
      }

      .hint {
        font-size: 12px;
        color: var(--gray-500);
      }

      input {
        padding: 12px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 16px;
        transition: border-color 0.2s;
      }

      input:focus {
        outline: none;
        border-color: var(--primary);
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }

      .btn-primary {
        padding: 12px;
        background: var(--primary);
        color: white;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 500;
        cursor: pointer;
        transition: background 0.2s;
        margin-top: var(--spacing-sm);
      }

      .btn-primary:hover:not(:disabled) {
        background: var(--primary-dark);
      }

      .btn-primary:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .register-footer {
        margin-top: var(--spacing-lg);
        text-align: center;
        font-size: 14px;
        color: var(--gray-500);
      }

      .register-footer a {
        color: var(--primary);
        text-decoration: none;
        font-weight: 500;
      }

      .register-footer a:hover {
        text-decoration: underline;
      }

      .legal-links {
        margin-top: var(--spacing-md);
        font-size: 12px;
        color: var(--gray-400);
      }

      .legal-links a {
        color: var(--gray-500);
        font-weight: 400;
      }
    `;
  }
}

// Register custom element
customElements.define('register-page', RegisterPage);
